/**
 * `HashtagDecoratorExtension` — Tiptap extension that finds `#hashtag`
 * patterns in the editor text and decorates each as an inline chip.
 *
 * Click handling is upstream's responsibility: the decoration tags each
 * span with `data-hashtag="<name>"`, and the editor consumer (e.g.
 * RichTextEditorPane) attaches a delegated click listener on `view.dom`
 * that catches clicks on `[data-hashtag]` elements and opens a modal.
 *
 * Why a single ProseMirror plugin (not Tiptap Mark / NodeView):
 *
 * - **No document mutation.** Decorations are pure visual overlay; the
 *   markdown body remains untouched. Marks would alter the document and
 *   round-trip differently through marked/turndown — `#tag` would become
 *   `[#tag](?)` or similar, polluting the user's saved markdown.
 * - **Cheap to re-derive.** The decoration set is rebuilt only when the
 *   doc changes (`appendTransaction` is doc-aware via tr.docChanged).
 *   Re-scanning text nodes is O(n); fine for notes up to ~100KB. Larger
 *   notes are rare; if they become a problem, throttle the scan.
 * - **No false-positive interference with paste / undo / IME.** Marks
 *   would shift cursor behaviour around the decorated text; decorations
 *   don't.
 *
 * Hashtag detection rule:
 *
 *   `(?<![\w])#([\w][\w-]*)`
 *
 *   - `(?<![\w])` — preceded by a non-word boundary so `URLs#fragment`
 *     and `commit#abc` don't match
 *   - `#`
 *   - `[\w]` — must start with a word char (so `## heading` doesn't trigger
 *     — the `#` is followed by a space, which is non-word)
 *   - `[\w-]*` — continues with word chars and dashes
 *
 * Skipped contexts:
 *   - `codeBlock` nodes — `#` is a comment in many languages; don't chip-ify
 *     hashes in code
 *   - `code` mark text — inline code spans
 *   - `heading` text — `# Heading` already lost the leading `#` by parse
 *     time (heading nodes don't contain it), but we ALSO skip ATX-style
 *     mid-heading hashes (`# A # B`) for sanity
 */

import { Extension } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { EditorState } from '@tiptap/pm/state';
import type { Node as PmNode } from '@tiptap/pm/model';
import { debouncedRecomputeView } from './decoratorRecompute';

interface PluginValue {
  enabled: boolean;
  decorations: DecorationSet;
}

export const hashtagDecoratorPluginKey = new PluginKey<PluginValue>(
  'hashtagDecorator',
);

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    hashtagDecorator: {
      /** Enable or disable hashtag chip decorations. When disabled, the
       *  decoration set is empty (rendered as plain text). */
      setHashtagsEnabled: (enabled: boolean) => ReturnType;
    };
  }
}

/** Word char + dash. `[\w]` in JS regex covers `[A-Za-z0-9_]`. */
const HASHTAG_REGEX = /(^|[^\w])#([A-Za-z][\w-]*)/g;

/**
 * Rebuild the decoration set by walking text nodes in the doc and applying
 * the hashtag regex to each. Skips `codeBlock` content and `code` inline
 * marks. Returns a fresh DecorationSet — cheap enough to rebuild per
 * docChange transaction.
 */
function computeDecorations(doc: PmNode): DecorationSet {
  const decorations: Decoration[] = [];
  doc.descendants((node, pos, parent) => {
    // Skip headings — the docstring always promised this but the check was
    // missing until U7 (2026-07-05): a mid-heading `#tag` decorated the title
    // text (same ransom-note effect as the entity chips).
    if (node.type.name === 'heading') return false;
    // Skip code blocks (the markdown `#` would otherwise decorate every
    // shell / python / markdown comment in the codebase).
    if (parent !== null && parent.type.name === 'codeBlock') return false;
    if (!node.isText) return true;
    // Skip if the text node has the `code` (inline) mark.
    if (node.marks.some((m) => m.type.name === 'code')) return true;
    const text = node.text ?? '';
    // Reset lastIndex per scan (HASHTAG_REGEX is module-scoped with `g`
    // flag — has to be reset to avoid skipping matches across nodes).
    HASHTAG_REGEX.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = HASHTAG_REGEX.exec(text)) !== null) {
      // Group 1 is the prefix char (or empty); group 2 is the hashtag name.
      const prefixLen = match[1].length;
      const name = match[2];
      const matchStart = match.index + prefixLen;
      const from = pos + matchStart;
      const to = from + 1 + name.length; // 1 for the `#` itself
      decorations.push(
        Decoration.inline(from, to, {
          class: 'hashtag-chip',
          'data-hashtag': name,
          // Tabindex makes it keyboard-focusable for accessibility;
          // role="link" so screen readers announce it correctly.
          tabindex: '0',
          role: 'link',
        }),
      );
    }
    return true;
  });
  return DecorationSet.create(doc, decorations);
}

export const HashtagDecoratorExtension = Extension.create({
  name: 'hashtagDecorator',

  addProseMirrorPlugins() {
    return [
      new Plugin<PluginValue>({
        key: hashtagDecoratorPluginKey,
        state: {
          init: (_, state: EditorState) => ({
            enabled: true,
            decorations: computeDecorations(state.doc),
          }),
          apply(tr, prev) {
            const meta = tr.getMeta(hashtagDecoratorPluginKey) as
              | { enabled?: boolean; recompute?: boolean }
              | undefined;
            const enabled = meta?.enabled ?? prev.enabled;
            if (!enabled) {
              // No chip decorations when disabled.
              return { enabled, decorations: DecorationSet.empty };
            }
            // Full re-scan ONLY on an explicit (debounced) recompute, or when
            // the toggle just flipped off → on. Everything else — including
            // every keystroke — just cheaply position-maps the existing set;
            // the debounced view dispatches a recompute shortly after typing
            // stops to pick up newly-typed hashtags (perf, 2026-06-10).
            if (meta?.recompute || (meta?.enabled === true && !prev.enabled)) {
              return { enabled, decorations: computeDecorations(tr.doc) };
            }
            if (tr.docChanged) {
              return {
                enabled,
                decorations: prev.decorations.map(tr.mapping, tr.doc),
              };
            }
            return { enabled, decorations: prev.decorations };
          },
        },
        view: debouncedRecomputeView(hashtagDecoratorPluginKey),
        props: {
          decorations(state) {
            return hashtagDecoratorPluginKey.getState(state)?.decorations;
          },
        },
      }),
    ];
  },

  addCommands() {
    return {
      setHashtagsEnabled:
        (enabled: boolean) =>
        ({ tr, dispatch }) => {
          if (dispatch !== undefined) {
            dispatch(tr.setMeta(hashtagDecoratorPluginKey, { enabled }));
          }
          return true;
        },
    };
  },
});
